export interface NetworkConnectivityCheck {
  isOnline: boolean;
  latency?: number;
  error?: string;
}

/**
 * Checks network connectivity by making a simple request to a reliable endpoint
 */
export const checkNetworkConnectivity = async (): Promise<NetworkConnectivityCheck> => {
  try {
    const startTime = performance.now();
    
    // Use a lightweight endpoint to check connectivity
    const response = await fetch('/favicon.ico', {
      method: 'HEAD',
      cache: 'no-store',
      signal: AbortSignal.timeout(5000), // 5 second timeout
    });
    
    const endTime = performance.now();
    const latency = endTime - startTime;
    
    return {
      isOnline: response.ok,
      latency,
    };
  } catch (error) {
    return {
      isOnline: false,
      error: error instanceof Error ? error.message : 'Unknown network error',
    };
  }
};

/**
 * Determines if an error is a network-related error
 */
export const isNetworkError = (error: any): boolean => {
  if (!error) return false;
  
  // Check for axios network errors
  if (error.code) {
    const networkErrorCodes = [
      'NETWORK_ERROR',
      'ECONNABORTED',
      'ENOTFOUND',
      'ECONNREFUSED',
      'ETIMEOUT',
      'ERR_NETWORK',
      'ERR_INTERNET_DISCONNECTED',
    ];
    if (networkErrorCodes.includes(error.code)) return true;
  }
  
  // Check for axios errors without response (network errors)
  if (error.isAxiosError && !error.response) return true;
  
  // Check for fetch network errors
  if (error instanceof TypeError && error.message.includes('fetch')) return true;
  
  // Check for common network error messages
  const networkErrorMessages = [
    'network error',
    'network request failed',
    'failed to fetch',
    'load failed',
    'connection refused',
    'timeout',
    'dns',
  ];
  
  const errorMessage = (error.message || '').toLowerCase();
  return networkErrorMessages.some(msg => errorMessage.includes(msg));
};

/**
 * Creates a delay for retry mechanisms
 */
export const delay = (ms: number): Promise<void> => {
  return new Promise(resolve => setTimeout(resolve, ms));
};

/**
 * Retry function with exponential backoff for network operations
 */
export const retryWithBackoff = async <T>(
  operation: () => Promise<T>,
  maxRetries: number = 3,
  baseDelay: number = 1000
): Promise<T> => {
  let lastError: any;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error;
      
      // Don't retry if it's not a network error
      if (!isNetworkError(error)) {
        throw error;
      }
      
      // Don't delay on the last attempt
      if (attempt < maxRetries) {
        const delayTime = baseDelay * Math.pow(2, attempt); // Exponential backoff
        await delay(delayTime);
      }
    }
  }
  
  throw lastError;
};

/**
 * Gets a user-friendly network error message
 */
export const getNetworkErrorMessage = (error: any): string => {
  if (isNetworkError(error)) {
    return 'Problema de conexão detectado. Verifique sua conexão com a internet e tente novamente.';
  }
  
  return 'Erro desconhecido. Tente novamente mais tarde.';
}; 